import {
	Button,
	ChangeTriggerKind,
	SearchInputForm,
	SearchPlaceholder,
	SelectAllCheckbox,
	SelectAllCheckboxValue
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import {
	EmailFilterSelector,
	EmailSortDirection,
	EmailSortOrder,
	FolderCounters,
	MessageReadState
} from "client/Api/Email/email";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { FilterOptionSelector } from "./FilterOptionSelector/FilterOptionSelector";
import styles from "./Header.module.less";
import { SortOptionSelector } from "./SortOptionSelector/SortOptionSelector";
import { MoreOptions } from "./MoreOptions";

export type HeaderProps = {
	smtpAddress: string;
	folderId: string;
	folderName: string;
	searchTerm: VersionedValue<string>;
	onSearch(searchTerm: VersionedValue<string>): void;
	sortOrder: Exclude<EmailSortOrder, EmailSortOrder.None>;
	sortDirection: EmailSortDirection;
	globalSelectionStatus: SelectAllCheckboxValue;
	filter: EmailFilterSelector | null;
	onComposeNewEmail(): void;
	onSortOrderChange(field: Exclude<EmailSortOrder, EmailSortOrder.None>): void;
	onSortDirectionChange(direction: EmailSortDirection): void;
	onFilterChange(filter: EmailFilterSelector | null): void;
	onNavigateToResults(): void;
	onGlobalSelectionChange(shouldSelectAll: boolean): void;
	counters?: FolderCounters;
	onRefreshList(): void;
	onFolderReadStatusChange(
		smtpAddress: string,
		folderId: string,
		status: MessageReadState
	): void;
	isMobile: boolean;
};

export const Header = (props: HeaderProps): ReactElement => {
	const {
		smtpAddress,
		folderId,
		folderName,
		searchTerm,
		onSearch,
		sortOrder,
		sortDirection,
		globalSelectionStatus,
		counters,
		filter,
		onComposeNewEmail,
		onGlobalSelectionChange,
		onSortOrderChange,
		onSortDirectionChange,
		onFilterChange,
		onNavigateToResults,
		onRefreshList,
		onFolderReadStatusChange,
		isMobile
	} = props;

	return (
		<div {...setTrackingId("header")} className={styles.root}>
			<h4
				{...setTrackingId("title")}
				className={styles.title}
			>
				{folderName}
			</h4>
			<div className={styles.createButton}>
				<Button
					text={localize("_NewEmailFull")}
					icon="plus"
					kind="primary"
					onClick={onComposeNewEmail}
					width="fill"
					trackingId="create-new-email"
					size="large"
				/>
			</div>
			<div className={styles.tools}>
				<div className={styles.searchForm}>
					<SearchInputForm
						value={searchTerm}
						onChange={(value, version) => onSearch({ value, version })}
						placeholder={
							<SearchPlaceholder
								kind="filter"
								properties={[
									localize("_Sender"),
									localize("_Subject"),
									localize("_Content")
								]}
							/>
						}
						trigger={{ kind: ChangeTriggerKind.Debounced }}
						onNavigateToResults={onNavigateToResults}
						trackingId="search-form"
						height="large"
						icon="filter"
					/>
				</div>
				<div className={styles.checkboxAndSort}>
					<div className={styles.checkbox}>
						<SelectAllCheckbox
							value={globalSelectionStatus}
							onChange={onGlobalSelectionChange}
							trackingId="select-all"
						/>
					</div>
					<SortOptionSelector
						field={sortOrder}
						direction={sortDirection}
						onFieldChange={onSortOrderChange}
						onDirectionChange={onSortDirectionChange}
					/>
				</div>
				<div className={styles.filterAndMore}>
					<FilterOptionSelector
						value={filter}
						onChange={onFilterChange}
						isMobile={isMobile}
					/>
					{counters && (
						<div className={styles.moreButton}>
							<MoreOptions
								smtpAddress={smtpAddress}
								folderId={folderId}
								counters={counters}
								onRefreshList={onRefreshList}
								onFolderReadStatusChange={onFolderReadStatusChange}
								isMobile={isMobile}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
Header.displayName="Header";